import axios from 'axios'
import { getAuthToken } from './authService'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add token to requests if available
api.interceptors.request.use(async (config) => {
  try {
    const token = await getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    // If token fetch fails, try localStorage directly
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
}, (error) => {
  return Promise.reject(error)
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored token on auth error
      localStorage.removeItem('auth_token')
      // Optionally redirect to login
      // window.location.href = '/admin/login'
    }
    return Promise.reject(error)
  }
)

// Projects API
export const projectsApi = {
  getAll: (filters = {}) => {
    // filters can include: category, tag, limit, status, include_drafts, project_plan_id
    const params = {}
    if (filters.category) params.category = filters.category
    if (filters.tag) params.tag = filters.tag
    if (filters.limit) params.limit = filters.limit
    if (filters.status) params.status = filters.status
    if (filters.include_drafts) params.include_drafts = filters.include_drafts
    if (filters.project_plan_id) params.project_plan_id = filters.project_plan_id
    return api.get('/projects', { params })
  },
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getTags: () => api.get('/projects/metadata/tags'),
}

// File upload API
export const filesApi = {
  upload: (file, onUploadProgress) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    })
  },
  delete: (fileUrl) => api.delete('/upload', { data: { file_url: fileUrl } }),
}

// Auth API - wraps authService for backward compatibility
export const authApi = {
  login: async (username, password) => {
    const { login } = await import('./authService')
    const result = await login(username, password)
    if (result.success) {
      return { data: { token: localStorage.getItem('auth_token'), user: result.user } }
    }
    throw new Error(result.error)
  },
  verify: () => api.get('/auth/verify'),
  logout: async () => {
    const { logout } = await import('./authService')
    await logout()
    return Promise.resolve()
  },
  me: () => api.get('/auth/me'),
}

// Categories API
export const categoriesApi = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
}

// Site Settings API
export const siteSettingsApi = {
  get: () => api.get('/site-settings'),
  update: (data) => api.put('/site-settings', data),
}

// Elise Section APIs
export const projectPlansApi = {
  getAll: (filters = {}) => {
    const params = {}
    if (filters.project_id) params.project_id = filters.project_id
    return api.get('/elise/project-plans', { params })
  },
  list: (filters = {}) => {
    const params = {}
    if (filters.project_id) params.project_id = filters.project_id
    return api.get('/elise/project-plans', { params })
  },
  getById: (id) => api.get(`/elise/project-plans/${id}`),
  create: (data) => api.post('/elise/project-plans', data),
  update: (id, data) => api.put(`/elise/project-plans/${id}`, data),
  delete: (id) => api.delete(`/elise/project-plans/${id}`),
  upload: (formData) => api.post('/elise/project-plans/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  reorder: (order) => api.post('/elise/project-plans/reorder', order),
  // Task management
  addTask: (planId, task) => api.post(`/elise/project-plans/${planId}/tasks`, task),
  updateTask: (planId, taskId, task) => api.put(`/elise/project-plans/${planId}/tasks/${taskId}`, task),
  deleteTask: (planId, taskId) => api.delete(`/elise/project-plans/${planId}/tasks/${taskId}`),
  reorderTasks: (planId, order) => api.post(`/elise/project-plans/${planId}/tasks/reorder`, order),
  // Milestone management
  addMilestone: (planId, milestone) => api.post(`/elise/project-plans/${planId}/milestones`, milestone),
  updateMilestone: (planId, milestoneId, milestone) => api.put(`/elise/project-plans/${planId}/milestones/${milestoneId}`, milestone),
  deleteMilestone: (planId, milestoneId) => api.delete(`/elise/project-plans/${planId}/milestones/${milestoneId}`),
  reorderMilestones: (planId, order) => api.post(`/elise/project-plans/${planId}/milestones/reorder`, order),
}

export const calendarApi = {
  getAll: (filters = {}) => {
    const params = {}
    if (filters.project_id) params.project_id = filters.project_id
    if (filters.start_date) params.start_date = filters.start_date
    if (filters.end_date) params.end_date = filters.end_date
    return api.get('/elise/calendar', { params })
  },
  getById: (id) => api.get(`/elise/calendar/${id}`),
  create: (data) => api.post('/elise/calendar', data),
  update: (id, data) => api.put(`/elise/calendar/${id}`, data),
  delete: (id) => api.delete(`/elise/calendar/${id}`),
  discoverAppleCalendars: () => api.post('/elise/calendar/apple-calendar/discover'),
  testAppleCalendarConnection: () => api.post('/elise/calendar/apple-calendar/test-connection'),
  syncWithAppleCalendar: () => api.post('/elise/calendar/apple-calendar/sync'),
  // Public calendar (ICS/webcal) endpoints
  testPublicCalendarConnection: () => api.post('/elise/calendar/public-calendar/test'),
  syncWithPublicCalendar: () => api.post('/elise/calendar/public-calendar/sync'),
  // Create event from milestone or task
  createFromMilestone: (data) => api.post('/elise/calendar/from-milestone', data),
  createFromTask: (data) => api.post('/elise/calendar/from-task', data),
  // Hosted calendar feed
  getFeedUrl: () => api.get('/elise/calendar/feed-url'),
}

export const dashboardApi = {
  getStats: () => api.get('/elise/dashboard/stats'),
}

export const ideasApi = {
  getAll: (filters = {}) => {
    const params = {}
    if (filters.status) params.status = filters.status
    if (filters.category) params.category = filters.category
    if (filters.priority) params.priority = filters.priority
    return api.get('/elise/ideas', { params })
  },
  list: (filters = {}) => {
    const params = {}
    if (filters.status) params.status = filters.status
    if (filters.category) params.category = filters.category
    if (filters.priority) params.priority = filters.priority
    return api.get('/elise/ideas', { params })
  },
  getById: (id) => api.get(`/elise/ideas/${id}`),
  create: (data) => api.post('/elise/ideas', data),
  update: (id, data) => api.put(`/elise/ideas/${id}`, data),
  delete: (id) => api.delete(`/elise/ideas/${id}`),
  convertToPlan: (id) => api.post(`/elise/ideas/${id}/convert-to-plan`),
}

// Inventory Management API
export const inventoryApi = {
  // Items
  getAll: (filters = {}) => {
    const params = {}
    if (filters.item_type) params.item_type = filters.item_type
    if (filters.category) params.category = filters.category
    if (filters.status) params.status = filters.status
    if (filters.location_id) params.location_id = filters.location_id
    if (filters.search) params.search = filters.search
    if (filters.low_stock_only) params.low_stock_only = filters.low_stock_only
    return api.get('/elise/inventory', { params })
  },
  getById: (id) => api.get(`/elise/inventory/${id}`),
  create: (data) => api.post('/elise/inventory', data),
  update: (id, data) => api.put(`/elise/inventory/${id}`, data),
  delete: (id) => api.delete(`/elise/inventory/${id}`),
  getStats: () => api.get('/elise/inventory/stats'),
  getCategories: () => api.get('/elise/inventory/categories/list'),
  restock: (id, quantity, notes) => api.post(`/elise/inventory/restock/${id}`, null, { params: { quantity, notes } }),

  // Storage Locations
  getLocations: () => api.get('/elise/inventory/locations'),
  createLocation: (data) => api.post('/elise/inventory/locations', data),
  updateLocation: (id, data) => api.put(`/elise/inventory/locations/${id}`, data),
  deleteLocation: (id) => api.delete(`/elise/inventory/locations/${id}`),

  // Usage Logs
  getUsageLogs: (itemId, days = 90) => api.get(`/elise/inventory/${itemId}/usage`, { params: { days } }),
  logUsage: (itemId, data) => api.post(`/elise/inventory/${itemId}/usage`, data),
  getConsumption: (itemId, days = 90) => api.get(`/elise/inventory/${itemId}/consumption`, { params: { days } }),
}

// Helper function for backward compatibility
export const fetchDashboardStats = async () => {
  const response = await dashboardApi.getStats()
  return response.data
}

export default api
